type PerformanceTripleMetric = {
  goal: number;
  pace: number;
  value: number;
};

type PerformanceSingleMetric = {
  value: number;
};

type PerformanceMetrics = {
  Sales: PerformanceTripleMetric;
  Pitches?: PerformanceSingleMetric;
  Hours?: PerformanceSingleMetric;
  Close: PerformanceTripleMetric;
  Install: PerformanceTripleMetric;
  Accounts?: PerformanceTripleMetric;
  SIT?: PerformanceTripleMetric;
  Leads?: PerformanceTripleMetric;
  CS?: PerformanceTripleMetric;
  Oppty?: PerformanceTripleMetric;
  Sit?: PerformanceTripleMetric;
  SRA?: PerformanceTripleMetric;
  'SRA (SGR)'?: PerformanceTripleMetric;
};

type PerformanceRow = {
  startDate: string;
  office: string;
  region: string;
  vp: string | null;
  coast: string;
  metrics: PerformanceMetrics;
};

type PerformancePeriodBuckets = {
  day: PerformanceRow[];
  week: PerformanceRow[];
  month: PerformanceRow[];
  quarter: PerformanceRow[];
};

type PerformanceScopeData = {
  current: PerformancePeriodBuckets;
  last: PerformancePeriodBuckets;
};

type PerformanceData = {
  me: PerformanceScopeData;
  team: PerformanceScopeData;
  region: PerformanceScopeData;
  coast: PerformanceScopeData;
  all: PerformanceScopeData;
  user_config: PerformanceUserConfig;
};

/** Scope buckets on `PerformanceData` (excludes `user_config`). */
type PerformanceScopeKey = Exclude<keyof PerformanceData, 'user_config'>;

type PerformanceUserConfig = {
  default_view: 'team' | 'region' | 'coast' | 'all' | 'me';
  role: 'LEADER' | 'SVP' | 'VP' | 'DIRECTOR' | 'SENIOR' | 'REP';
};

const DEFAULT_PERFORMANCE_SCOPES: readonly PerformanceScopeKey[] = [
  'me',
  'team',
];

const PERFORMANCE_SCOPE_BY_ROLE: Partial<
  Record<PerformanceUserConfig['role'], readonly PerformanceScopeKey[]>
> = {
  LEADER: ['me', 'team', 'region'],
  SENIOR: ['me', 'team', 'region'],
  DIRECTOR: ['me', 'team', 'region', 'coast'],
  VP: ['me', 'team', 'region', 'coast', 'all'],
  SVP: ['me', 'team', 'region', 'coast', 'all'],
  REP: ['me', 'team'],
};

function getPerformanceScopesForRole(
  role: PerformanceUserConfig['role'] | undefined,
): PerformanceScopeKey[] {
  if (!role) {
    return [...DEFAULT_PERFORMANCE_SCOPES];
  }
  const scopes = PERFORMANCE_SCOPE_BY_ROLE[role];
  return scopes ? [...scopes] : [...DEFAULT_PERFORMANCE_SCOPES];
}

function resolvePerformanceScopeSelection(
  userConfig: PerformanceUserConfig | undefined,
  allowed: readonly PerformanceScopeKey[],
): PerformanceScopeKey {
  if (!userConfig) {
    return allowed[0] ?? 'me';
  }
  if (allowed.includes(userConfig.default_view)) {
    return userConfig.default_view;
  }
  return allowed[0] ?? 'me';
}

type LeaderboardRangeKey =
  | 'qtr'
  | 'mtd'
  | 'wtd'
  | 'today'
  | 'yesterday'
  | 'last_week'
  | 'last_month'
  | 'last_quarter'
  | 'custom';

type LeaderboardAggregatedMetric = {
  value: number | string;
  pace: number;
};

type LeaderboardMetricValue = LeaderboardAggregatedMetric | number;

type LeaderboardAggregatedMetrics = {
  CS?: LeaderboardMetricValue;
  SRA?: LeaderboardMetricValue;
  CAP?: LeaderboardMetricValue;
  IC?: LeaderboardMetricValue;
  X?: number;
  OPPTY?: number;
  SIT?: number;
  SRA_SGR?: number;
};

type LeaderboardRepMetrics = {
  CS?: number;
  SRA?: number;
  CAP?: number;
  IC?: number;
  LG?: number;
  SIT?: number;
  OPPTY?: number;
  SRA_SGR?: number;
  X?: number;
};

type LeaderboardMetricsByRange<T> = Partial<Record<LeaderboardRangeKey, T>>;

type LeaderboardAggregatedEntry = {
  entity_id: string;
  name: string;
  subtitle?: string;
  title?: string | null;
  office?: string | null;
  region?: string | null;
  vp?: string;
  director?: string | null;
  metro_market?: string | null;
  team?: string | null;
  metrics_by_range: LeaderboardMetricsByRange<LeaderboardAggregatedMetrics>;
};

type LeaderboardRepEntry = {
  entity_id: string;
  name: string;
  subtitle?: string;
  title?: string | null;
  office?: string;
  region?: string;
  squad?: string;
  team?: string | null;
  metro_market?: string | null;
  director?: string | null;
  status: string;
  metrics_by_range: LeaderboardMetricsByRange<LeaderboardRepMetrics>;
  vp?: string;
};

type LeaderboardViewScopeKey =
  | 'rep'
  | 'squad'
  | 'office'
  | 'region'
  | 'vp'
  | 'metro_market'
  | 'team';

type LeaderboardData = {
  user_region?: string;
  user_team?: string;
  user_squad?: string;
  vp?: LeaderboardAggregatedEntry[];
  region?: LeaderboardAggregatedEntry[];
  metro_market?: LeaderboardAggregatedEntry[];
  team?: LeaderboardAggregatedEntry[];
  office?: LeaderboardAggregatedEntry[];
  squad?: LeaderboardRepEntry[];
  rep?: LeaderboardRepEntry[];
};

type PerformanceProfileData = {
  EmployeeId: string;
  RunXStatus: {
    CurrentRank: string;
    CurrentRankValue?: number;
    PacingRank?: string;
    PacingRankValue?: number;
    LevelUpYesNo?: string;
    LevelUp?: number;
    MaxRank?: string;
    MaxRankValue?: number;
    PreviousRank?: string;
    CurrentSraPoints?: string;
    CurrentIcPoints?: string;
  };
  RecruiterStatus?: {
    CurrentRank: string;
    PreviousRank: string;
  } | null;
};

export type {
  PerformanceTripleMetric,
  PerformanceSingleMetric,
  PerformanceMetrics,
  PerformanceRow,
  PerformancePeriodBuckets,
  PerformanceScopeData,
  PerformanceData,
  PerformanceScopeKey,
  PerformanceUserConfig,
  LeaderboardRangeKey,
  LeaderboardAggregatedMetric,
  LeaderboardAggregatedMetrics,
  LeaderboardRepMetrics,
  LeaderboardAggregatedEntry,
  LeaderboardRepEntry,
  LeaderboardViewScopeKey,
  LeaderboardData,
  PerformanceProfileData,
};

export { getPerformanceScopesForRole, resolvePerformanceScopeSelection };
